import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useSetupStatus from '../../hooks/useSetupStatus';
import safeStorage from '../../utils/safeStorage';
import { 
  VpnKey as Key,
  Person as User,
  CheckCircle,
  ArrowForward as ArrowRight,
  AutoAwesome as Sparkles,
  ChevronLeft,
  ChevronRight,
  Warning as AlertCircle,
  Lock,
  Visibility as Eye,
  VisibilityOff as EyeOff
} from '@mui/icons-material';
import { Box, Typography, Button, Container, Stepper, Step, StepLabel } from '@mui/material';
import ProductKeyFromFacturation from '../../components/setup/ProductKeyFromFacturation';
import config from '../../config';

const TravelITSetupWizard = () => {
  const navigate = useNavigate();
  const { isSetupComplete, refreshSetupStatus } = useSetupStatus();
  const [currentStep, setCurrentStep] = useState(0);
  const [licenseValidated, setLicenseValidated] = useState(false);


  // 🔄 VÉRIFIER LICENCE ET COMPTE AU DÉMARRAGE
  useEffect(() => {
    const checkExistingSetup = async () => {
      try {
        // DEMO MODE: Auto-validate license for demo access
        console.log('🎭 DEMO MODE: Auto-validating license for demo access');
        setLicenseValidated(true);
        
        // Vérifier s'il y a une licence existante
        const storedLicense = safeStorage.getItem('travelit_license');
        if (storedLicense) {
          const licenseInfo = JSON.parse(storedLicense);
          if (licenseInfo.key && licenseInfo.status === 'active') {
            console.log('🔑 Licence TravelIT existante trouvée');
            setLicenseValidated(true);
            setCurrentStep(2); // Aller à création compte
            return;
          }
        }

        // Skip API check - will be handled by useSetupStatus hook

        // Check centralized setup status
        if (isSetupComplete) {
          console.log('👤 Compte admin TravelIT déjà configuré (cached)');
          setTimeout(() => {
            console.log('🎯 Redirecting to login page...');
            navigate('/login', { replace: true });
          }, 500);
          return;
        }
        
        console.log('🚀 Première installation TravelIT - affichage wizard');
      } catch (error) {
        console.error('Erreur lors de la vérification setup:', error);
      }
    };

    checkExistingSetup();
  }, [navigate]);

  // 🔑 CALLBACK WHEN LICENSE IS VALIDATED
  const onLicenseValidated = () => {
    console.log('🎉 TravelIT: Licence validée avec succès');
    setLicenseValidated(true);
    setTimeout(() => {
      setCurrentStep(2); // Passer à la création de compte
    }, 1000);
  };

  const steps = [
    {
      id: 'welcome',
      title: 'Bienvenue sur TravelIT',
      icon: Sparkles,
      component: 'WelcomeStep'
    },
    {
      id: 'license',
      title: 'Clé de produit',
      icon: Key,
      component: 'ProductKey'
    },
    {
      id: 'profile',
      title: 'Profil utilisateur',
      icon: User,
      component: 'RegisterAccount'
    }
  ];

  const nextStep = () => {
    // DEMO MODE: Skip license step if already validated
    if (currentStep === 0 && licenseValidated) {
      setCurrentStep(2); // Skip directly to account creation
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = async () => {
    // 🛡️ NAVIGATION INTELLIGENTE: Gérer le retour selon l'état du setup
    if (currentStep === 2 && licenseValidated) {
      console.log('🔄 [SETUP] User clicked back from account creation step');
      
      // Check if setup is already complete (admin already exists)
      try {
        const currentStatus = await refreshSetupStatus();
        
        if (currentStatus?.isSetupComplete) {
          console.log('✅ [SETUP] Setup already complete, redirecting to login');
          navigate('/login', { 
            replace: true,
            state: {
              message: 'Configuration déjà terminée. Connectez-vous avec vos identifiants.',
              type: 'info'
            }
          });
          return;
        }
      } catch (error) {
        console.error('❌ [SETUP] Error checking setup status:', error);
      }
      
      // If setup not complete, ask user if they want to exit setup
      const userWantsToExit = window.confirm(
        'Voulez-vous quitter la configuration et aller à la page de connexion?\n\n' +
        'Note: Vous devrez terminer la configuration plus tard pour utiliser TravelIT.'
      );
      
      if (userWantsToExit) {
        console.log('🚪 [SETUP] User chose to exit setup, going to login');
        navigate('/login', { 
          replace: true,
          state: {
            message: 'Configuration non terminée. Vous devez créer un compte administrateur pour continuer.',
            type: 'warning'
          }
        });
      } else {
        console.log('🔄 [SETUP] User chose to stay in setup');
      }
      return;
    }

    // Normal back navigation for other steps
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 🎨 WELCOME STEP COMPONENT
  const WelcomeStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      style={{ textAlign: 'center', padding: '2rem' }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          background: 'rgba(91, 108, 249, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 2rem',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <img 
          src="./images/logo.png" 
          alt="TravelIT Logo" 
          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
          onError={(e) => {
            e.target.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.style.cssText = 'width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #fff;';
            fallback.textContent = 'T';
            e.target.parentNode.replaceChild(fallback, e.target);
          }}
        />
      </motion.div>

      <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
        Bienvenue sur TravelIT
      </Typography>
      
      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3, maxWidth: '600px', mx: 'auto' }}>
        Système de Gestion pour Agences de Voyage
      </Typography>

      <Box sx={{ mb: 4, maxWidth: '500px', mx: 'auto' }}>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
          Configurons ensemble votre agence de voyage avec un système sécurisé et professionnel.
        </Typography>
      </Box>

      <motion.div 
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Button
          variant="contained"
          onClick={nextStep}
          endIcon={<ArrowRight />}
          sx={{ 
            borderRadius: '16px',
            background: 'linear-gradient(to right, #2563eb, #9333ea)',
            px: 4,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)',
            '&:hover': {
              background: 'linear-gradient(to right, #1e40af, #7e22ce)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(37, 99, 235, 0.4)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          Commencer la Configuration
        </Button>
      </motion.div>
    </motion.div>
  );

  // 👤 ACCOUNT CREATION STEP COMPONENT - EXACTEMENT COMME FACTURATION
  const RegisterAccountStep = () => {
    const [formData, setFormData] = useState({
      username: '',
      password: '',
      confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Clear error when user types
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    };

    const validateFields = () => {
      const newErrors = {};
      
      if (!formData.username || formData.username.trim().length < 3) {
        newErrors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
      }
      
      if (!formData.password || formData.password.length < 6) {
        newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!validateFields()) {
        return;
      }

      try {
        setIsLoading(true);
        setErrors({});

        console.log('🔄 TravelIT: Création du compte administrateur...');
        
        // Detect standalone client mode
        const isStandaloneClient = window.electronAPI && !window.location.href.includes('localhost');
        
        if (isStandaloneClient) {
          console.log('🎯 [SETUP] Standalone client mode - creating admin account locally');
          
          // Create admin account locally for standalone client
          const adminData = {
            id: 1,
            username: formData.username,
            nom: formData.username.toUpperCase(),
            prenom: formData.username,
            email: `${formData.username}@travelit.local`,
            password: formData.password, // Store plaintext for standalone mode
            role: 'Admin',
            estActif: true,
            createdAt: new Date().toISOString()
          };
          
          // Store admin data in local storage for standalone mode
          safeStorage.setItem('travelit_admin_user', JSON.stringify(adminData));
          safeStorage.setItem('travelit_setup_complete', 'true');
          safeStorage.setItem('setup_username', formData.username);
          
          console.log('✅ [SETUP] Admin account created locally:', formData.username);
          
          // Update setup status to complete
          await refreshSetupStatus();
          
          // Show success message
          setErrors({
            success: `✅ Configuration terminée ! Votre identifiant de connexion est : ${formData.username}`
          });
          
          // Redirect to login after delay
          setTimeout(() => {
            console.log('🎯 [SETUP] Redirecting to login page...');
            navigate('/login', { 
              replace: true,
              state: {
                message: `Configuration terminée ! Connectez-vous avec l'identifiant : ${formData.username}`,
                type: 'success',
                username: formData.username
              }
            });
          }, 2000);
          
        } else {
          // Development mode - use backend API
          console.log('🔄 [SETUP] Development mode - using backend API');
          
          const response = await fetch(`${config.apiUrl}/setup/admin`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              username: formData.username,
              password: formData.password,
              confirmPassword: formData.confirmPassword
            })
          });
          
          const result = await response.json();
          
          if (result.success) {
            console.log('✅ TravelIT: Compte administrateur créé avec succès');
            console.log('👤 Utilisateur créé:', result.user);
            
            // Clear any cached login state to force fresh auth
            safeStorage.removeItem('travelit_logged_in');
            
            // Save username for login page reference
            safeStorage.setItem('setup_username', formData.username);
            
            // Refresh setup status cache and wait for propagation
            console.log('🔄 [SETUP] Refreshing setup status...');
            await refreshSetupStatus();
            console.log('✅ [SETUP] Setup status refreshed and propagated');
            
            // Show success message with username
            setErrors({
              success: `✅ Configuration terminée ! Votre identifiant de connexion est : ${formData.username}`
            });
            
            // Add extra delay to ensure all components receive the updated setup status
            setTimeout(() => {
              console.log('🎯 [SETUP] Redirecting to login page...');
              
              navigate('/login', {
                replace: true,
                state: {
                  message: `Configuration terminée ! Connectez-vous avec l'identifiant : ${formData.username}`,
                  type: 'success',
                  username: formData.username
                }
              });
            }, 2000); // Reduced time but still enough for state propagation
          } else {
            console.error('❌ Erreur création admin:', result.error);
            setErrors({ submit: result.error || 'Erreur lors de la création du compte' });
          }
        }

      } catch (error) {
        setErrors({ submit: 'Erreur lors de la création du compte' });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="setup-card"
        style={{ maxWidth: '500px', margin: '0 auto' }}
      >
        <div className="setup-header">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="welcome-logo"
          >
            <img 
              src="./images/logo.png" 
              alt="TravelIT Logo" 
              style={{ 
                width: '40px', 
                height: '40px', 
                objectFit: 'contain' 
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.style.cssText = 'width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: bold; color: #fff;';
                fallback.textContent = 'T';
                e.target.parentNode.replaceChild(fallback, e.target);
              }}
            />
          </motion.div>
          
          <h1 className="setup-title">Créer un compte administrateur</h1>
          <p className="setup-subtitle">
            Configurez votre compte administrateur pour accéder à TravelIT
          </p>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="setup-form"
          onSubmit={handleSubmit}
        >
          {errors.submit && (
            <div className="error-message">
              <AlertCircle sx={{ fontSize: 16 }} />
              {errors.submit}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="username">Nom d'utilisateur *</label>
            <div className="input-wrapper">
              <User className="input-icon" sx={{ fontSize: 18 }} />
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Ex: admin_travel"
                className={errors.username ? 'error' : ''}
                disabled={isLoading}
              />
            </div>
            {errors.username && <span className="error-text">{errors.username}</span>}
            <div className="help-text" style={{
              fontSize: '0.85em',
              color: 'rgba(255, 255, 255, 0.6)',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Key sx={{ fontSize: 14 }} />
              Ce nom sera votre identifiant de connexion à TravelIT
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="password">Mot de passe *</label>
            <div className="input-wrapper">
              <Lock className="input-icon" sx={{ fontSize: 18 }} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Votre mot de passe"
                className={errors.password ? 'error' : ''}
                disabled={isLoading}
              />
              <button 
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
              </button>
            </div>
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
            <div className="input-wrapper">
              <Lock className="input-icon" sx={{ fontSize: 18 }} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirmer le mot de passe"
                className={errors.confirmPassword ? 'error' : ''}
                disabled={isLoading}
              />
              <button 
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? <EyeOff sx={{ fontSize: 18 }} /> : <Eye sx={{ fontSize: 18 }} />}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>

          {/* Boutons d'action */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', marginTop: '2rem' }}>
            <button
              type="button"
              onClick={prevStep}
              className="primary-button"
              style={{ 
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                width: 'auto',
                flex: '0 0 auto',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem'
              }}
              disabled={isLoading}
              title={licenseValidated ? "Quitter la configuration et aller à la connexion" : "Étape précédente"}
            >
              <ChevronLeft sx={{ fontSize: 18 }} />
              {licenseValidated ? 'Quitter' : 'Retour'}
            </button>

            <button
              type="submit"
              className="primary-button"
              disabled={isLoading}
              style={{ flex: 1 }}
            >
              {isLoading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    style={{ marginRight: '8px' }}
                  >
                    <User sx={{ fontSize: 18 }} />
                  </motion.div>
                  Création en cours...
                </>
              ) : (
                <>
                  <User sx={{ fontSize: 18 }} />
                  Créer mon compte administrateur
                  <ChevronRight sx={{ fontSize: 18 }} />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </motion.div>
    );
  };

  // 🔧 RENDER COMPONENT BASED ON CURRENT STEP
  const renderCurrentStep = () => {
    // DEMO MODE: Skip license step if already validated
    if (currentStep === 1 && licenseValidated) {
      setCurrentStep(2); // Skip to account creation
      return null;
    }
    switch (currentStep) {
      case 0:
        return <WelcomeStep />;
      case 1:
        return (
          <ProductKeyFromFacturation 
            onLicenseValidated={onLicenseValidated}
            onBack={prevStep}
            onNext={nextStep}
          />
        );
      case 2:
        return <RegisterAccountStep />;
      default:
        return <WelcomeStep />;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #3730a3 50%, #581c87 100%)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src="./images/logo.png" 
              alt="TravelIT Logo" 
              style={{ height: '32px', width: 'auto' }}
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 6px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: bold; color: #fff;';
                fallback.textContent = 'T';
                e.target.parentNode.replaceChild(fallback, e.target);
              }}
            />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
              Configuration TravelIT
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Progress Stepper */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Container maxWidth="md">
          <Stepper activeStep={currentStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id}>
                <StepLabel 
                  sx={{
                    '& .MuiStepLabel-label': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-active': { color: '#fff' },
                      '&.Mui-completed': { color: '#4ade80' }
                    },
                    '& .MuiStepIcon-root': {
                      color: 'rgba(255, 255, 255, 0.3)',
                      '&.Mui-active': { color: '#3b82f6' },
                      '&.Mui-completed': { color: '#4ade80' }
                    }
                  }}
                >
                  {step.title}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 4 }}>
        <Container maxWidth="lg">
          {/* 🎨 CARD TRANSPARENTE COMME FACTURATION - PAS DE BACKGROUND SOMBRE */}
          <div style={{ 
            padding: 0,
            background: 'transparent', // ✅ TRANSPARENT comme FACTURATION
            border: 'none',
            boxShadow: 'none'
          }}>
            {renderCurrentStep()}
          </div>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          TravelIT v1.0.0 — Configuration Initiale Sécurisée
        </Typography>
      </Box>
    </Box>
  );
};

export default TravelITSetupWizard;
